import {
  Box,
  Grid,
  Space,
  Text,
  Title,
  TextInput,
  ScrollArea,
  Image,
  HoverCard,
  Flex,
  Stack,
  Group,
  Paper,
  ThemeIcon,
  Anchor,
  ActionIcon,
  Popover,
  UnstyledButton,
  Center,
  Loader,
} from "@mantine/core";
import { FC, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import {
  GetManyOrdersOfUserDocument,
  GetManyOrdersOfUserQuery,
  OrderStatus,
  SortOrder,
  UserType,
  NullsOrder,
} from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { DashboardV2 } from "./DashboardV2";
import { OrderSummaryRow } from "/@/components/order/OrderSummaryRow";
import { useQuery, useMutation } from "@apollo/client";
import { useDebounce } from "use-debounce";
import {
  IconSearch,
  IconInfoCircle,
  IconChevronRight,
  IconCircleXFilled,
} from "@tabler/icons-react";
import {
  getPatientLabelFirstLine,
  ItemType,
  UPDATE_ORDER,
  NEW_PAP_WORKFLOW_ACTIVATION_DATE,
} from "@jasper/shared";
import cameraDashboardImg from "/@/assets/images/camera-dashboard.png";
import noNotifDashboardImg from "/@/assets/images/no-notif-dashboard.png";
import { CreateOrderStep } from "/@/shared/types/orders/createOrder.types";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import { useCombinedWorkflowOrderStore } from "/@/shared/stores/orders/create-combined-workflow-store.store";
import { SearchResult } from "./SearchResult";

const DashboardV1: FC = () => {
  type DashboardOrder = GetManyOrdersOfUserQuery["getManyOrdersOfUser"][number];

  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [searchInput, setSearchInput] = useState("");
  const [searchInputDebounce] = useDebounce(searchInput, 500);

  const {
    updateCreateOrderStep,
    updateCreateProductStep,
    updateCreateProductIndex,
    updateProductNewWFStep,
  } = useCreateOrderStore();

  const { updateNewCombinedOrderId, updateCombinedWorkflowProducts } =
    useCombinedWorkflowOrderStore();

  const [updateOrder] = useMutation(UPDATE_ORDER);

  // Query pour les notifications (commandes draft) - sans limite pour avoir le vrai total
  const { data: draftOrdersData, refetch: refetchDraftOrders } = useQuery(
    GetManyOrdersOfUserDocument,
    {
      fetchPolicy: "network-only",
      variables: {
        where: {
          status: {
            equals: OrderStatus.Draft,
          },
        },
        orderBy: [
          {
            updatedAt: SortOrder.Desc,
          },
        ],
      },
    }
  );

  // Query pour la recherche de patients
  const { data: searchResults } = useQuery(GetManyOrdersOfUserDocument, {
    fetchPolicy: "network-only",
    skip: searchInputDebounce.length === 0,
    variables: {
      where: {
        status: {
          not: {
            equals: OrderStatus.Draft,
          },
        },
        AND: [
          {
            OR: [
              ...searchInputDebounce
                .trim()
                .split(" ")
                .reduce((acc: any[], word: string) => {
                  acc.push(
                    ...[
                      {
                        patient: {
                          is: {
                            firstName: {
                              contains: word,
                              mode: "insensitive",
                            },
                          },
                        },
                      },
                      {
                        patient: {
                          is: {
                            lastName: {
                              contains: word,
                              mode: "insensitive",
                            },
                          },
                        },
                      },
                      {
                        orderReference: {
                          contains: word,
                          mode: "insensitive",
                        },
                      },
                    ]
                  );
                  return acc;
                }, []),
            ],
          },
        ],
      },
      take: 5,
      orderBy: [
        {
          confirmedAt: {
            nulls: NullsOrder.Last,
            sort: SortOrder.Desc,
          },
        },
        {
          updatedAt: SortOrder.Desc,
        },
      ],
    },
  });

  // Query pour les commandes en cours
  const { data: ordersInProgressData } = useQuery(GetManyOrdersOfUserDocument, {
    fetchPolicy: "network-only",
    variables: {
      where: {
        status: {
          not: {
            in: [OrderStatus.Draft, OrderStatus.Delivered],
          },
        },
      },
      take: 5,
      orderBy: [
        {
          updatedAt: SortOrder.Desc,
        },
      ],
    },
  });

  // Query pour les commandes terminées (pour compléter si moins de 5 en cours)
  const { data: completedOrdersData } = useQuery(GetManyOrdersOfUserDocument, {
    fetchPolicy: "network-only",
    variables: {
      where: {
        status: {
          equals: OrderStatus.Delivered,
        },
      },
      take: 5,
      orderBy: [
        {
          updatedAt: SortOrder.Desc,
        },
      ],
    },
  });

  // Combiner les commandes : d'abord les en cours, puis compléter avec les terminées
  const displayedOrders = useMemo(() => {
    const inProgress = ordersInProgressData?.getManyOrdersOfUser ?? [];
    const completed = completedOrdersData?.getManyOrdersOfUser ?? [];

    if (inProgress.length >= 5) {
      return inProgress.slice(0, 5);
    }

    const needed = 5 - inProgress.length;
    return [...inProgress, ...completed.slice(0, needed)];
  }, [
    ordersInProgressData?.getManyOrdersOfUser,
    completedOrdersData?.getManyOrdersOfUser,
  ]);

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 15 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  if (!ordersInProgressData || !draftOrdersData || !completedOrdersData) {
    return null;
  }

  const currentDateAndTimeString = currentTime.toLocaleString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const finishOrder = (order: DashboardOrder) => {
    updateCombinedWorkflowProducts(
      ((order?.products ?? [])
        ?.map(item => item.productType as string as ItemType)
        .filter(Boolean) ?? []) as ItemType[]
    );
    updateNewCombinedOrderId(order.id);
    updateCreateProductIndex(0);
    updateCreateProductStep(0);
    updateProductNewWFStep(0);
    const hasPartialDenturesWithRemovableItems = (order?.products ?? []).some(
      product =>
        ((product?.productType ?? null) as string as ItemType | null) ===
        ItemType.PARTIAL_DENTURE
    );

    const shouldUseNewWorkflow =
      hasPartialDenturesWithRemovableItems &&
      new Date(order.createdAt).getTime() >
        NEW_PAP_WORKFLOW_ACTIVATION_DATE.getTime();
    if (shouldUseNewWorkflow) {
      updateCreateOrderStep(CreateOrderStep.Product);
      navigate("/create-new-order", {
        state: {
          id: order.id,
          workflow: (order?.products ?? []).map(item => item.productType) ?? [],
          cameFromOtherProduct: false,
        },
      });
    } else {
      updateCreateOrderStep(CreateOrderStep.Patient);
      navigate("/create-order", {
        state: {
          id: order.id,
        },
      });
    }
  };

  return (
    <Flex p="lg" direction="column" h="100%" w="100%">
      {/* Header avec date et bonjour en haut de la page */}
      <Box mb="md">
        <Text size="sm" c="dimmed" fw={600} mb="xs">
          {currentDateAndTimeString}
        </Text>
        <Title order={2} c="dark.7" fw={700}>
          Bonjour{" "}
          {user?.type === UserType.Dentist && !user?.managedUserGroup
            ? "Dr."
            : ""}{" "}
          {user?.firstName} {user?.lastName}
        </Title>
      </Box>

      {/* Grille principale avec barre de recherche et notifications */}
      <Grid gap="lg" mb="xl" align="flex-end">
        <Grid.Col span={7}>
          <Paper withBorder radius="lg" p="lg" mih={220} mah={220}>
            <Grid>
              <Grid.Col span={8}>
                <Title order={3} variant="display">
                  Retrouvez facilement{" "}
                  <Text span inherit c="brand">
                    vos dossiers patients
                  </Text>
                </Title>
                <Text size="md" c="dimmed" mb="xl">
                  Retrouvez facilement les dossiers de vos patients en
                  recherchant ci-dessous.
                </Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Flex justify="center" align="center" h="100%">
                  <Image
                    src={cameraDashboardImg}
                    alt="Caméra intra-orale"
                    fit="contain"
                    maw={140}
                    w="100%"
                  />
                </Flex>
              </Grid.Col>
            </Grid>
          </Paper>

          <Box mt="md">
            <Popover
              opened={searchInput.length > 0}
              onDismiss={() => setSearchInput("")}
              width="target"
              position="bottom-start"
              withArrow
              shadow="md"
              radius="md"
            >
              <Popover.Target>
                <TextInput
                  placeholder="Chercher une référence ou un patient"
                  size="lg"
                  radius="lg"
                  leftSection={<IconSearch size={18} />}
                  value={searchInput}
                  onChange={event => setSearchInput(event.currentTarget.value)}
                />
              </Popover.Target>

              <Popover.Dropdown p={0}>
                {
                  <ScrollArea
                    h={
                      searchResults?.getManyOrdersOfUser.length
                        ? 260
                        : undefined
                    }
                    type="auto"
                    mah={260}
                  >
                    {searchResults?.getManyOrdersOfUser.length === 0 ? (
                      <Text c="dimmed" ta="center" py="md">
                        Aucun patient trouvé
                      </Text>
                    ) : (
                      <Stack gap={0}>
                        {searchResults?.getManyOrdersOfUser.map(order => (
                          <SearchResult key={order.id} order={order} />
                        ))}
                      </Stack>
                    )}
                  </ScrollArea>
                }
              </Popover.Dropdown>
            </Popover>
          </Box>
        </Grid.Col>

        <Grid.Col span={5}>
          <Paper
            withBorder
            radius="lg"
            p="lg"
            h="calc(220px + var(--mantine-spacing-md) + 52px)"
          >
            <Title order={3} mb="sm" mt={0} c="dark.7" fw={700} lineClamp={1}>
              Vous{" "}
              {draftOrdersData?.getManyOrdersOfUser.length === 0
                ? "n'avez aucune commande non terminée"
                : draftOrdersData?.getManyOrdersOfUser.length === 1
                  ? "avez 1 commande non terminée"
                  : `avez ${draftOrdersData?.getManyOrdersOfUser.length} commandes non terminées`}
            </Title>
            {(draftOrdersData?.getManyOrdersOfUser ?? []).length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                mih={200}
                p="xl"
              >
                <Image
                  src={noNotifDashboardImg}
                  alt="Aucune notification"
                  fit="contain"
                  maw={140}
                />
                <Text c="dimmed" mt="md">
                  Aucune notification
                </Text>
              </Flex>
            ) : (
              <ScrollArea h="calc(100% - 48px)" type="auto">
                {draftOrdersData?.getManyOrdersOfUser.map((order, index) => {
                  const deleteOrder = async () => {
                    try {
                      await updateOrder({
                        variables: {
                          where: { id: order.id },
                          data: { isArchived: { set: true } },
                        },
                      });
                      notifications.show({
                        title: "Commande supprimée",
                        color: "green",
                        message: "",
                      });
                      refetchDraftOrders();
                    } catch (e) {
                      console.error(e);
                      notifications.show({
                        title: "Impossible de supprimer la commande",
                        color: "red",
                        message: "",
                      });
                    }
                  };

                  return (
                    <Box key={order.id}>
                      <Group align="center" gap="md" py="md" wrap="nowrap">
                        <ThemeIcon
                          size={36}
                          radius="xl"
                          color="blue"
                          variant="filled"
                        >
                          <IconInfoCircle size={20} />
                        </ThemeIcon>
                        <Text
                          size="sm"
                          flex={1}
                          truncate="end"
                          onClick={() => finishOrder(order)}
                        >
                          Commande pour {getPatientLabelFirstLine(order)} non
                          terminée
                        </Text>
                        <HoverCard width={280} shadow="md">
                          <HoverCard.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label="Annuler la commande"
                              onClick={deleteOrder}
                            >
                              <IconCircleXFilled size={18} />
                            </ActionIcon>
                          </HoverCard.Target>
                          <HoverCard.Dropdown>
                            <UnstyledButton onClick={deleteOrder}>
                              <Text size="sm">
                                Cliquez-ici pour annuler la commande
                              </Text>
                            </UnstyledButton>
                          </HoverCard.Dropdown>
                        </HoverCard>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => finishOrder(order)}
                          aria-label="Ouvrir la commande"
                        >
                          <IconChevronRight size={18} />
                        </ActionIcon>
                      </Group>
                      {index <
                        draftOrdersData.getManyOrdersOfUser.length - 1 && (
                        <Box h={1} bg="gray.2" />
                      )}
                    </Box>
                  );
                })}
              </ScrollArea>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Section commandes en cours */}
      <Box w="100%">
        {displayedOrders.length === 0 ? (
          <Paper withBorder radius="lg" p="xl">
            <Group justify="space-between" align="center">
              <Title order={3} c="dark.7" fw={700}>
                Vos commandes
              </Title>
              <Anchor
                c="dark"
                fw={600}
                size="sm"
                onClick={() => navigate("/orders")}
              >
                Voir tout
              </Anchor>
            </Group>
            <Space h="md" />
            <Text ta="center" c="dimmed">
              Aucune commande disponible pour le moment
            </Text>
          </Paper>
        ) : (
          <Paper withBorder radius="lg" p="md">
            <Group justify="space-between" align="center">
              <Title order={3} c="dark.7" fw={700}>
                {(ordersInProgressData?.getManyOrdersOfUser.length ?? 0) > 0
                  ? "Commandes en cours"
                  : "Vos commandes"}
              </Title>
              <Anchor
                c="dark"
                fw={600}
                size="sm"
                onClick={() => navigate("/orders")}
              >
                Voir tout
              </Anchor>
            </Group>
            <Space h="md" />
            <ScrollArea type="auto">
              {displayedOrders.map((order, index, array) => (
                <OrderSummaryRow
                  key={order.id}
                  order={order}
                  variant="compact"
                  isLast={index === array.length - 1}
                />
              ))}
            </ScrollArea>
          </Paper>
        )}
      </Box>
    </Flex>
  );
};

export const Dashboard: FC = () => {
  const isThemeV2Enabled = useFeatureFlagEnabled("theme_v2");

  if (isThemeV2Enabled === undefined) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  return isThemeV2Enabled ? <DashboardV2 /> : <DashboardV1 />;
};
